// Admin — User detail (Wave E).

import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, KeyRound, Pencil } from 'lucide-react';
import { PageHeader } from '../../../components/ui/PageHeader/PageHeader';
import { Card } from '../../../components/ui/Card/Card';
import { Button } from '../../../components/ui/Button/Button';
import { Badge } from '../../../components/ui/Badge/Badge';
import { Spinner } from '../../../components/ui/Spinner/Spinner';
import { useStaffDetail, useTriggerPasswordReset } from './hooks/useUsers';
import styles from './UserFormPage.module.css';

const ROLE_LABELS: Record<string, string> = {
  staff: 'Staff',
  manager: 'Manager',
  admin: 'Admin',
};

function formatDate(iso: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString(undefined, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

export function UserDetailPage(): JSX.Element {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const detail = useStaffDetail(id);
  const reset = useTriggerPasswordReset();

  if (detail.isLoading) {
    return (
      <div className={styles.page}>
        <PageHeader title="User" />
        <Card>
          <div className={styles.empty}>
            <Spinner />
          </div>
        </Card>
      </div>
    );
  }

  if (!detail.data) {
    return (
      <div className={styles.page}>
        <PageHeader title="User" />
        <Card>
          <div className={styles.empty}>User not found.</div>
        </Card>
      </div>
    );
  }

  const user = detail.data;

  return (
    <div className={styles.page}>
      <PageHeader
        title={user.name}
        subtitle={`@${user.username} · ${ROLE_LABELS[user.role] ?? user.role}`}
        actions={
          <div className={styles.actions}>
            <Button
              variant="ghost"
              icon={ArrowLeft}
              onClick={() => navigate('/admin/users')}
            >
              Back
            </Button>
            <Button
              variant="secondary"
              icon={KeyRound}
              onClick={() => reset.mutate(user.uuid)}
              loading={reset.isPending}
            >
              Send Password Reset
            </Button>
            <Button
              variant="primary"
              icon={Pencil}
              onClick={() =>
                navigate(`/admin/users/${encodeURIComponent(user.uuid)}/edit`)
              }
            >
              Edit
            </Button>
          </div>
        }
      />

      <Card title="Profile">
        <div className={styles.detailGrid}>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Name</span>
            <span className={styles.detailValue}>{user.name}</span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Username</span>
            <span className={styles.detailValue}>{user.username}</span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Email</span>
            <span className={styles.detailValue}>{user.email}</span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Phone</span>
            <span className={styles.detailValue}>{user.phone ?? '—'}</span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Postcode</span>
            <span className={styles.detailValue}>{user.postcode ?? '—'}</span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Address</span>
            <span className={styles.detailValue}>{user.address ?? '—'}</span>
          </div>
        </div>
      </Card>

      <Card title="Account">
        <div className={styles.detailGrid}>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Role</span>
            <span className={styles.detailValue}>
              <Badge variant={user.role === 'admin' ? 'info' : 'default'}>
                {ROLE_LABELS[user.role] ?? user.role}
              </Badge>
            </span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Status</span>
            <span className={styles.detailValue}>
              {user.status === 'active' ? (
                <Badge variant="success">Active</Badge>
              ) : (
                <Badge variant="muted">Deactivated</Badge>
              )}
            </span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Last Login</span>
            <span className={styles.detailValue}>
              {formatDate(user.last_login_at)}
            </span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Password Changed</span>
            <span className={styles.detailValue}>
              {formatDate(user.last_password_changed_at)}
            </span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Member Since</span>
            <span className={styles.detailValue}>
              {formatDate(user.created_at)}
            </span>
          </div>
          <div className={styles.detailItem}>
            <span className={styles.detailLabel}>Force Password Change</span>
            <span className={styles.detailValue}>
              {user.force_password_change ? 'Yes' : 'No'}
            </span>
          </div>
        </div>
      </Card>
    </div>
  );
}
